/**
 * A recording as the scoring route takes it.
 *
 * `/user/pronounce/` is sent 16-bit little-endian mono samples at 16 kHz, in
 * base64. A browser records at whatever rate its device runs, so the page
 * resamples first, and this turns the floating-point samples Web Audio gives
 * into those bytes. `src/user/pronunciation-request.ts` is the other side.
 */

/** The rate the route takes, in samples a second. */
export const routeSampleRate = 16_000;

/**
 * Samples in -1 to 1 as 16-bit little-endian integers.
 *
 * A sample past either end is clipped to it, since a loud attempt is still an
 * attempt.
 */
export function pcm16(samples: Float32Array): Uint8Array {
  const bytes = new Uint8Array(samples.length * 2);
  const view = new DataView(bytes.buffer);

  for (const [at, sample] of samples.entries()) {
    const clipped = Math.max(-1, Math.min(1, sample));

    view.setInt16(at * 2, Math.round(clipped * 0x7f_ff), true);
  }

  return bytes;
}

/** Bytes as base64, a block at a time so a long recording fits in a call. */
export function base64Of(bytes: Uint8Array): string {
  const block = 0x80_00;
  let text = "";

  for (let at = 0; at < bytes.length; at += block) {
    text += String.fromCodePoint(...bytes.subarray(at, at + block));
  }

  return btoa(text);
}
